import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Modal, Badge } from 'react-bootstrap';
import { DatePicker } from "../datePicker";
import SearchDropdown from '../Settings/TaskManagementSettings/SearchDropdown';
import { displayToastMsg } from '../toastMessages/toastmessages';

interface EditTaskModalProps {
    showEditModal: boolean;
    setShowEditModal: (show: boolean) => void;
    taskToEdit: any;
    taskDate: string;
    setTaskDate: (date: string) => void;
    selectedUsers: string[];
    handleSaveTask: (updatedDescription?: string) => void;
    handleRemoveTask: (task: any) => void;
    // Delete modal props
    showDeleteModal: boolean;
    setShowDeleteModal: (show: boolean) => void;
    /** True when the linked undertaking issue supports auto-email (defect type 32_2). */
    allowsAutoEmail?: boolean;
    allLawyers: any[];
}

const EditUndertakingModal: React.FC<EditTaskModalProps> = ({
    showEditModal,
    setShowEditModal,
    taskToEdit,
    taskDate,
    setTaskDate,
    selectedUsers,
    handleSaveTask,
    handleRemoveTask,
    showDeleteModal,
    setShowDeleteModal,
    allowsAutoEmail = false,
    allLawyers,
}) => {

    // Function to extract the display title from the full title
    const getDisplayTitle = () => {
        const fullTitle = taskToEdit?.title || '';
        const secondBracketRegex = /.*\(([^)]*)\)(?!.*\([^)]*\))/;
        const matches = fullTitle.match(secondBracketRegex);
        return matches ? matches[1] : fullTitle;
    };

    // Create a wrapper function to handle the save and show toast
    const handleSaveWithToast = () => {
    
        handleSaveTask();
        
        // Display a success toast message
        displayToastMsg({
            id: Date.now(),
            type: "success",
            msg: "Task Updated Successfully"
        }, null);
    };

    const handleRemoveWithToast = (task) => {
        handleRemoveTask(task);
        // Display a success toast for deletion
        displayToastMsg({
            id: Date.now(),
            type: "success",
            msg: "Task Removed Successfully"
        }, null);
    };

    return (
        <>
            {/* Edit Task Modal */}
            <Modal size="sm" className="modal-sm-long" centered show={showEditModal} onHide={() => {
                setShowEditModal(false);
            }}>
                <Modal.Header closeButton={false}>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <h2>
                            {(taskToEdit?.overwriteDate === 1 || taskToEdit?.overwritePerson === 1) ? (
                                <>
                                    Task <small style={{ color: 'var(--txt-40)' }}><i>(edited)</i></small>
                                </>
                            ) : (
                                'Edit Task'
                            )}
                        </h2>
                        {taskToEdit && taskToEdit.typeID > 1000 && (
                            <Badge bg="success">User</Badge>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Task Description</Form.Label>
                            <Form.Control
                                type="text"
                                disabled
                                value={getDisplayTitle()}
                                readOnly
                                aria-readonly="true"
                            />
                            <div className="text-muted small mt-1">
                                <i className="bi bi-lock me-1" aria-hidden />
                                This task is linked to an undertaking; the description cannot be edited here.
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Assigned Users</Form.Label>
                            <SearchDropdown
                                allValues={[]}
                                selectedValues={selectedUsers}
                                onValueChange={() => {return}}
                                fullValuesList={allLawyers.map(user => ({
                                    Name: user.Name,
                                    Id: user.SubUserId?.toString() || user.UserID?.toString(),
                                    LSUCID: user.LSUCID
                                }))}
                                disabled={true}
                            />
                            <div className="text-muted small mt-1">
                                <i className="bi bi-lock me-1" aria-hidden />
                                Assigned users cannot be changed here for undertaking tasks.
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Task Date</Form.Label>
                            <DatePicker
                                date={taskDate}
                                setDate={(newDate) => setTaskDate(newDate)}
                                placeholder="Select date"
                                disabled={taskToEdit?.typeID == 303}
                            />
                        </Form.Group>
                    </Form>
                    <Button variant="link" className="me-auto text-danger" onClick={() => setShowDeleteModal(true)}>
                        <i className="fa-regular fa-trash-can status-danger"></i> Remove task from file
                    </Button>
                </Modal.Body>

                <Modal.Footer className="justify-content-between">
                    <Button variant="text" onClick={() => {
                        setShowEditModal(false);
                    }}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveWithToast}>
                        Update
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Task Confirmation Modal */}
            <Modal centered size="sm" show={showDeleteModal && showEditModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <h2>Remove Task</h2>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-warning">
                        This cannot be undone.
                    </div>
                    {allowsAutoEmail ? (
                        <p>Removing this task will turn off the associated auto-email for this undertaking. Are you sure you want to continue?</p>
                    ) : (
                        <p>Are you sure you want to continue?</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => handleRemoveWithToast(taskToEdit)}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditUndertakingModal; 